import React, { useState, useEffect, useRef, useCallback, MouseEvent as ReactMouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ArrowLeft, CheckCircle, AlertCircle, Loader, ChevronLeft,
  ChevronRight, Edit2, Check, X, GripVertical, Trash2,
} from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { graphqlClient } from '../services/graphqlClient';
import { localS3Overrides } from '../services/localS3';
import { ConfirmModal } from '../components/Modal';
import type { File as FileType, Document, UploadCredentials } from '../graphql/types';

// Point pdfjs worker to the matching version from node_modules (served by Vite)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const CONFIDENCE_THRESHOLD = 0.8;

interface ExtractedField {
  field: string;
  value: string;
  confidence: number;
  page: number;
  boundingBox?: { top: number; left: number; width: number; height: number };
}

interface ReviewField extends ExtractedField {
  accepted: boolean;
  editedValue?: string;
  editing: boolean;
}

interface LocationState {
  file: FileType;
  documents: Document[];
  uploadBucket: string;
  uploadCredentials: UploadCredentials | null;
}

const FileReview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // All documents' fields merged into one flat list for the file
  const [reviewFields, setReviewFields] = useState<ReviewField[]>([]);
  // Map documentId -> its fields slice indices
  const [docFieldMap, setDocFieldMap] = useState<Map<string, { doc: Document; start: number; end: number }>>(new Map());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<Document | null>(null);

  // Highlighted bounding boxes for current page
  const [highlights, setHighlights] = useState<Array<{
    top: number; left: number; width: number; height: number; fieldIndex: number; active: boolean;
  }>>([]);

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Resizable split panel state (PDF viewer width as percentage, default 75%)
  const [pdfPanelWidth, setPdfPanelWidth] = useState(75);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Load PDF and BDA data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!state) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    if (!state) return;
    const { file, documents, uploadBucket, uploadCredentials } = state;

    try {
      const region = import.meta.env.VITE_AWS_REGION || 'us-west-2';

      // Helper to create S3 client with given credentials
      const createS3Client = (creds: { accessKeyId: string; secretAccessKey: string; sessionToken?: string }) =>
        new S3Client({ region, credentials: creds, ...localS3Overrides() });

      // Helper to fetch PDF with given S3 client
      const fetchPdfWithClient = async (s3: S3Client): Promise<ArrayBuffer> => {
        const pdfPresignedUrl = await getSignedUrl(
          s3,
          new GetObjectCommand({ Bucket: uploadBucket, Key: file.s3Key }),
          { expiresIn: 3600 }
        );
        console.log('[FileReview] Presigned URL bucket:', uploadBucket, 'key:', file.s3Key);
        console.log('[FileReview] Presigned URL (first 100 chars):', pdfPresignedUrl.substring(0, 100));
        const pdfResp = await fetch(pdfPresignedUrl);
        console.log('[FileReview] S3 response status:', pdfResp.status, 'content-type:', pdfResp.headers.get('content-type'));
        if (!pdfResp.ok) throw new Error(`HTTP ${pdfResp.status}`);
        return pdfResp.arrayBuffer();
      };

      let pdfArrayBuffer: ArrayBuffer;

      // Try uploadCredentials first if available, fall back to identity pool if they fail (expired)
      if (uploadCredentials) {
        console.log('[FileReview] Trying uploadCredentials from job...');
        try {
          const s3 = createS3Client({
            accessKeyId: uploadCredentials.accessKeyId,
            secretAccessKey: uploadCredentials.secretAccessKey,
            sessionToken: uploadCredentials.sessionToken,
          });
          pdfArrayBuffer = await fetchPdfWithClient(s3);
          console.log('[FileReview] PDF loaded with uploadCredentials');
        } catch (err) {
          console.warn('[FileReview] uploadCredentials failed (likely expired), falling back to identity pool:', err);
          const session = await fetchAuthSession();
          const c = session.credentials!;
          const s3 = createS3Client({ accessKeyId: c.accessKeyId, secretAccessKey: c.secretAccessKey, sessionToken: c.sessionToken });
          pdfArrayBuffer = await fetchPdfWithClient(s3);
          console.log('[FileReview] PDF loaded with identity pool credentials');
        }
      } else {
        console.log('[FileReview] Using identity pool credentials');
        const session = await fetchAuthSession();
        console.log('[FileReview] Session credentials:', session.credentials ? 'present' : 'MISSING');
        if (!session.credentials) {
          throw new Error('No credentials available from identity pool');
        }
        const c = session.credentials;
        const s3 = createS3Client({ accessKeyId: c.accessKeyId, secretAccessKey: c.secretAccessKey, sessionToken: c.sessionToken });
        pdfArrayBuffer = await fetchPdfWithClient(s3);
      }

      // Build review fields directly from extractedFields (geometry now included from post-BDA processing)
      const allFields: ReviewField[] = [];
      const map = new Map<string, { doc: Document; start: number; end: number }>();

      // Sort documents by starting page number (from pageRange like "1-3" or "4-5")
      const sortedDocuments = [...documents].sort((a, b) => {
        const aStart = parseInt(a.pageRange?.split('-')[0] || '1', 10);
        const bStart = parseInt(b.pageRange?.split('-')[0] || '1', 10);
        return aStart - bStart;
      });

      for (const doc of sortedDocuments) {
        const parsedFields: ExtractedField[] = doc.extractedFields
          ? JSON.parse(doc.extractedFields)
          : [];

        console.log(`[FileReview] Document ${doc.documentId} (pages ${doc.pageRange}) has ${parsedFields.length} fields`);
        if (parsedFields.length > 0) {
          console.log('[FileReview] Sample field:', JSON.stringify(parsedFields[0], null, 2));
        }

        const start = allFields.length;
        for (const f of parsedFields) {
          allFields.push({
            field: (f as any).field_name ?? (f as any).field ?? '',
            value: (f as any).field_value ?? (f as any).value ?? '',
            confidence: (f as any).confidence_score ?? (f as any).confidence ?? 0,
            page: (f as any).page ?? 1,  // 1-based absolute PDF page from backend
            boundingBox: (f as any).bounding_box ?? (f as any).boundingBox ?? null,
            accepted: ((f as any).confidence_score ?? (f as any).confidence ?? 0) >= CONFIDENCE_THRESHOLD,
            editing: false,
          });
        }
        map.set(doc.documentId, { doc, start, end: allFields.length });
      }

      console.log(`[FileReview] Total fields: ${allFields.length}, fields with boundingBox: ${allFields.filter(f => f.boundingBox).length}`);

      setReviewFields(allFields);
      setDocFieldMap(map);

      // Load PDF from ArrayBuffer
      await loadPdf(pdfArrayBuffer);
    } catch (err) {
      console.error('Failed to load review data:', err);
      setPdfError('Failed to load file. Please try again.');
      setPdfLoading(false);
    }
  };

  const loadPdf = async (data: ArrayBuffer) => {
    try {
      const pdf = await pdfjsLib.getDocument({ data }).promise;
      pdfRef.current = pdf;
      setTotalPages(pdf.numPages);
      console.log('[FileReview] PDF loaded, pages:', pdf.numPages, 'setting pdfLoading=false');
      setPdfLoading(false);
    } catch (err) {
      console.error('[FileReview] PDF parse error:', err);
      setPdfError('Failed to load PDF.');
      setPdfLoading(false);
    }
  };

  // ── Render PDF page ───────────────────────────────────────────────────────
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfRef.current || !canvasRef.current) return;

    // Cancel any in-progress render
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    const page = await pdfRef.current.getPage(pageNum);
    const viewport = page.getViewport({ scale: pdfScale });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    setCanvasSize({ width: viewport.width, height: viewport.height });

    const task = page.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (e: any) {
      if (e?.name !== 'RenderingCancelledException') console.error(e);
    }
  }, [pdfScale]);

  useEffect(() => {
    if (!pdfLoading && pdfRef.current) renderPage(currentPage);
  }, [currentPage, pdfLoading, renderPage]);

  // ── Compute highlights for current page ──────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Use offsetWidth/offsetHeight — the actual CSS display size the overlay is positioned against.
    // getBoundingClientRect includes scroll offset and can be wrong when the canvas is inside
    // a scrollable container.
    const displayWidth = canvas.offsetWidth;
    const displayHeight = canvas.offsetHeight;

    if (canvasWidth === 0 || canvasHeight === 0) return;

    if (activeFieldIndex === null) {
      setHighlights([]);
      return;
    }

    const f = reviewFields[activeFieldIndex];
    if (!f || f.page !== currentPage || !f.boundingBox) {
      setHighlights([]);
      return;
    }

    const box = {
      top: f.boundingBox.top * displayHeight,
      left: f.boundingBox.left * displayWidth,
      width: f.boundingBox.width * displayWidth,
      height: f.boundingBox.height * displayHeight,
      fieldIndex: activeFieldIndex,
      active: true,
    };

    setHighlights([box]);
  }, [reviewFields, currentPage, activeFieldIndex, canvasSize]);

  // ── Field actions ─────────────────────────────────────────────────────────
  const acceptField = (i: number) => {
    setReviewFields(prev => prev.map((f, idx) =>
      idx === i ? { ...f, accepted: true, editing: false } : f
    ));
  };

  const startEdit = (i: number) => {
    setReviewFields(prev => prev.map((f, idx) =>
      idx === i ? { ...f, editing: true, editedValue: f.editedValue ?? f.value } : f
    ));
  };

  const commitEdit = (i: number) => {
    setReviewFields(prev => prev.map((f, idx) =>
      idx === i ? { ...f, editing: false, value: f.editedValue ?? f.value, accepted: true, confidence: 1.0 } : f
    ));
  };

  const cancelEdit = (i: number) => {
    setReviewFields(prev => prev.map((f, idx) =>
      idx === i ? { ...f, editing: false } : f
    ));
  };

  // ── Delete a detected document ──────────────────────────────────────────────
  // Removes the document (and its extracted fields) from the file, reindexing
  // the remaining field slices so the panel stays consistent.
  const handleDeleteDocument = async (doc: Document) => {
    setConfirmDeleteDoc(null);
    setDeletingDocId(doc.documentId);
    setSaveError(null);
    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload['custom:tenantId'] as string) ||
        'default-tenant';
      await graphqlClient.mutations.deleteDocument(
        doc.documentId,
        doc.fileId,
        doc.loanId,
        doc.jobId,
        tenantId
      );

      const entry = docFieldMap.get(doc.documentId);
      const start = entry?.start ?? 0;
      const end = entry?.end ?? 0;
      const removed = end - start;

      if (entry) {
        setReviewFields(prev => [...prev.slice(0, start), ...prev.slice(end)]);
      }
      setDocFieldMap(prev => {
        const next = new Map<string, { doc: Document; start: number; end: number }>();
        for (const [id, e] of prev.entries()) {
          if (id === doc.documentId) continue;
          if (e.start >= end) {
            next.set(id, { doc: e.doc, start: e.start - removed, end: e.end - removed });
          } else {
            next.set(id, e);
          }
        }
        return next;
      });
      setActiveFieldIndex(null);
    } catch (err) {
      console.error('Failed to delete document:', err);
      setSaveError('Failed to delete document. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  // ── Finalize ──────────────────────────────────────────────────────────────
  const needsReview = reviewFields.filter(f => f.confidence < CONFIDENCE_THRESHOLD && !f.accepted);
  const canFinalize = needsReview.length === 0;

  const handleFinalize = async () => {
    if (!state || !canFinalize) return;
    setSaving(true);
    setSaveError(null);

    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

      // Group corrected fields by documentId so the lambda can write them
      // back to each document's extracted_fields (where downstream agents read from)
      const documentFields: Record<string, Array<{ field: string; value: string; confidence: number }>> = {};
      for (const [docId, { start, end }] of docFieldMap.entries()) {
        documentFields[docId] = reviewFields.slice(start, end).map(f => ({
          field: f.field,
          value: f.value,
          confidence: f.confidence,
        }));
      }

      await graphqlClient.mutations.finalizeDocumentReview(
        state.file.fileId, state.file.loanId, state.file.jobId, tenantId,
        JSON.stringify(documentFields)
      );

      navigate(-1);
    } catch (err) {
      console.error('Failed to finalize review:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Jump to field's page when clicking in the panel ───────────────────────
  const focusField = (i: number) => {
    const f = reviewFields[i];
    setActiveFieldIndex(i);
    if (f.page !== undefined && f.page !== currentPage) setCurrentPage(f.page);
  };

  // ── Resizable panel handlers ──────────────────────────────────────────────
  const handleResizeStart = (e: ReactMouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      // Clamp between 30% and 85%
      setPdfPanelWidth(Math.min(85, Math.max(30, newWidth)));
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (!state) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No review data. Please navigate from a job.</p>
      </div>
    );
  }

  const { file } = state;

  return (
    <div className="flex flex-col h-screen max-h-screen overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </button>
          <span className="text-gray-300">|</span>
          <h1 className="text-sm font-semibold text-gray-900 truncate max-w-xs">
            {file.filename}
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          {needsReview.length > 0 && (
            <span className="text-xs text-yellow-600">
              {needsReview.length} field{needsReview.length !== 1 ? 's' : ''} need review
            </span>
          )}
          {saveError && (
            <span className="text-xs text-red-500">{saveError}</span>
          )}
          <button
            onClick={handleFinalize}
            disabled={!canFinalize || saving}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: canFinalize ? '#3730A3' : undefined }}
            title={!canFinalize ? 'Accept all low-confidence fields before finalizing' : undefined}
          >
            {saving
              ? <><Loader className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              : <><CheckCircle className="h-4 w-4 mr-2" />Finalize data</>}
          </button>
        </div>
      </div>

      {/* Body */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* ── Left: PDF viewer ── */}
        <div
          className="flex flex-col border-r border-gray-200 bg-gray-100 overflow-hidden"
          style={{ width: `${pdfPanelWidth}%` }}
        >
          {/* PDF toolbar */}
          <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-gray-600">
                {currentPage} / {totalPages || '—'}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center space-x-1">
              {[0.75, 1.0, 1.25, 1.5].map(s => (
                <button
                  key={s}
                  onClick={() => setPdfScale(s)}
                  className={`px-2 py-0.5 text-xs rounded ${pdfScale === s ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100 text-gray-600'}`}
                >
                  {Math.round(s * 100)}%
                </button>
              ))}
            </div>
          </div>

          {/* Canvas area */}
          <div className="flex-1 overflow-auto flex justify-center p-4">
            {pdfLoading && (
              <div className="flex items-center justify-center w-full h-full">
                <Loader className="h-8 w-8 animate-spin" style={{ color: '#3730A3' }} />
              </div>
            )}
            {pdfError && (
              <div className="flex items-center justify-center w-full h-full">
                <div className="text-center">
                  <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-red-500">{pdfError}</p>
                </div>
              </div>
            )}
            {!pdfLoading && !pdfError && (
              <div ref={canvasContainerRef} className="relative inline-block shadow-lg">
                <canvas ref={canvasRef} />
                {/* Bounding box overlays */}
                {highlights.map((h, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveFieldIndex(h.fieldIndex)}
                    style={{
                      position: 'absolute',
                      top: h.top,
                      left: h.left,
                      width: h.width,
                      height: h.height,
                      border: `2px solid ${h.active ? '#3730A3' : '#6366f1'}`,
                      backgroundColor: h.active ? 'rgba(55,48,163,0.15)' : 'rgba(99,102,241,0.08)',
                      cursor: 'pointer',
                      borderRadius: 2,
                      transition: 'background-color 0.15s',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Resizer handle ── */}
        <div
          onMouseDown={handleResizeStart}
          className="w-2 flex-shrink-0 bg-gray-200 hover:bg-indigo-300 cursor-col-resize flex items-center justify-center transition-colors"
          title="Drag to resize"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>

        {/* ── Right: Fields panel ── */}
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          <div className="px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0">
            <p className="text-xs text-gray-500">
              {reviewFields.filter(f => f.accepted).length} / {reviewFields.length} fields accepted
            </p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {Array.from(docFieldMap.entries()).map(([docId, { doc, start, end }]) => (
              <div key={docId}>
                {/* Document section header */}
                <div className="px-4 py-2 bg-gray-50 sticky top-0 z-10 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {doc.documentType || 'Document'} — pages {doc.pageRange}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteDoc(doc);
                    }}
                    disabled={deletingDocId === doc.documentId}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Delete this detected document"
                  >
                    {deletingDocId === doc.documentId ? (
                      <Loader className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                {reviewFields.slice(start, end).map((f, relIdx) => {
                  const absIdx = start + relIdx;
                  const lowConf = f.confidence < CONFIDENCE_THRESHOLD;
                  const isActive = absIdx === activeFieldIndex;
                  return (
                    <div
                      key={f.field}
                      onClick={() => focusField(absIdx)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${
                        isActive
                          ? 'bg-indigo-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-500 mb-0.5">
                            {f.field.replace(/_/g, ' ')}
                          </p>
                          {f.editing ? (
                            <div className="flex items-center gap-1">
                              <input
                                autoFocus
                                value={f.editedValue ?? f.value}
                                onChange={e => setReviewFields(prev => prev.map((x, i) =>
                                  i === absIdx ? { ...x, editedValue: e.target.value } : x
                                ))}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') commitEdit(absIdx);
                                  if (e.key === 'Escape') cancelEdit(absIdx);
                                }}
                                className="flex-1 text-sm px-2 py-1 border border-indigo-400 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                onClick={e => e.stopPropagation()}
                              />
                              <button onClick={e => { e.stopPropagation(); commitEdit(absIdx); }} className="p-1 text-green-600 hover:text-green-700"><Check className="h-4 w-4" /></button>
                              <button onClick={e => { e.stopPropagation(); cancelEdit(absIdx); }} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-900 break-words">
                              {f.value || <span className="italic text-gray-400">empty</span>}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs font-medium ${
                              f.confidence >= CONFIDENCE_THRESHOLD
                                ? 'text-green-600'
                                : 'text-yellow-600'
                            }`}>
                              {(f.confidence * 100).toFixed(1)}%
                            </span>
                            {lowConf && !f.accepted && (
                              <span className="inline-flex items-center text-xs text-yellow-600">
                                <AlertCircle className="h-3 w-3 mr-0.5" />needs review
                              </span>
                            )}
                            {f.accepted && (
                              <span className="inline-flex items-center text-xs text-green-600">
                                <CheckCircle className="h-3 w-3 mr-0.5" />accepted
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Actions */}
                        {!f.editing && (
                          <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                            <button
                              onClick={e => { e.stopPropagation(); startEdit(absIdx); }}
                              className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                              title="Edit value"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            {!f.accepted && (
                              <button
                                onClick={e => { e.stopPropagation(); acceptField(absIdx); }}
                                className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                title="Accept value"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {f.accepted && (
                              <span className="p-1.5 text-green-500">
                                <CheckCircle className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {reviewFields.length === 0 && (
              <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                No extracted fields found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Delete Document Modal */}
      {confirmDeleteDoc && (
        <ConfirmModal
          title="Delete Document"
          message={`Delete the detected ${
            confirmDeleteDoc.documentType || 'document'
          }${
            confirmDeleteDoc.pageRange ? ` (pages ${confirmDeleteDoc.pageRange})` : ''
          }? This removes the detected document and its extracted data. This action cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => void handleDeleteDocument(confirmDeleteDoc)}
          onCancel={() => setConfirmDeleteDoc(null)}
        />
      )}
    </div>
  );
};

export default FileReview;
